// Shared HUD building blocks: filigree frames, medallions, source chips, claim labels,
// data-age wrappers, the floating tooltip, and the full-screen shell.

import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useId, useLayoutEffect, useRef } from 'preact/hooks';
import type { DateStr, SourceId } from '../core/schema.ts';
import type { Relation } from '../world/api.ts';
import { RELATION_COLOR, RELATION_LABEL, luminance, shade } from './color.ts';
import { useServices } from './context.ts';
import { chipText, fmtDate, isStale } from './format.ts';
import { Icon, type IconName } from './icons.tsx';
import { registerSources, sourceFocus, tip, type TipState } from './store.ts';

// ─── Filigree corners ────────────────────────────────────────────────────────────────────
//
//   ◆━━━━━•━━━━━━━          one ornament per corner, mirrored by CSS:
//   ┃ ╭──────────           diamond at the corner, a double rule on each arm,
//   • │  @                  a bead on each arm and a scroll curl inside.
//   ┃ │

function CornerSvg({ pos }: { pos: 'tl' | 'tr' | 'bl' | 'br' }) {
  return (
    <svg class={`wc-corner wc-corner--${pos}`} viewBox="0 0 40 40" fill="none" stroke="currentColor" aria-hidden="true">
      <path d="M6 11V39M11 6H39" stroke-width="1.6" />
      <path d="M10.5 33V16.5q0-6 6-6H33" stroke-width="0.9" stroke-opacity="0.6" />
      <path d="M6 .8L11.2 6 6 11.2.8 6z" fill="currentColor" stroke="none" />
      <circle cx="6" cy="6" r="1.5" fill="#1a1206" stroke="none" />
      <circle cx="24" cy="6" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="6" cy="24" r="1.6" fill="currentColor" stroke="none" />
      <path d="M14.5 21c-.6-3.6 1.4-6.4 4.6-6.6 2.6-.2 4.3 1.7 3.9 3.8-.4 1.8-2.4 2.6-3.7 1.6-1-.8-.7-2.2.4-2.4" stroke-width="1.1" />
    </svg>
  );
}

export function Corners() {
  return (
    <>
      <CornerSvg pos="tl" />
      <CornerSvg pos="tr" />
      <CornerSvg pos="bl" />
      <CornerSvg pos="br" />
    </>
  );
}

/** Translucent teal panel with the gold double rule and corner filigree. */
export function Frame(props: { class?: string; large?: 'large'; style?: JSX.CSSProperties; children: ComponentChildren }) {
  const cls = ['wc-panel', props.large ? 'wc-frame--lg' : '', props.class ?? ''].join(' ');
  return (
    <div class={cls} style={props.style}>
      <Corners />
      {props.children}
    </div>
  );
}

// ─── Medallion ───────────────────────────────────────────────────────────────────────────
//
//   gold bezel with ticks ─┐   ┌─ relation ring (optional)
//                          ◯ ◯ ◯
//   disc: flag image, or the primary colour with a secondary-colour hairline;
//   embossed mark (shadow layer below, highlight above), film grain, inner shadow.

const MEDAL_TICKS = 48;
const LAUREL_LEAVES = 7;

interface MedallionProps {
  colors: { primary: string; secondary: string };
  /** 1–3 characters drawn in the centre (leader initial or ISO3). */
  mark: string;
  size: number;
  /** Relation ring colour; omitted = plain gold. */
  relation?: Relation;
  /** Laurel wreath and gold lettering for playable leaders. */
  laurel?: 'laurel';
  /** Flag image clipped to the disc (states); the mark is not drawn over it. */
  flag?: string;
  /** A glyph in place of the mark, for actors that have neither a flag nor a short code (non-state parties, blocs). */
  glyph?: IconName;
  badge?: IconName;
  title?: string;
}

// Glyph drawn in the disc: its edge in viewBox units.
const GLYPH_SIZE = 46;

// Laurel wreath: on each side a stem arcs from the bottom up the rim; leaves sprout from it,
// alternating outward and inward, shrinking toward the tip.
const LAUREL_R = 31.5;
const LAUREL_START = 14;
const LAUREL_SWEEP = 112;
const LEAF_SPLAY = 38;

// Mark sizes in viewBox units by length; three letters are fitted to a fixed width.
const MARK_FONT = [44, 44, 31, 23];
const MARK_FIT = 1.32;
const EMBOSS_DY = 1.1;
const LIGHT_DISC = 0.55; // disc luminance above which leader lettering turns dark bronze

function laurelBranch(side: 1 | -1, color: string): JSX.Element {
  const leaves: JSX.Element[] = [];
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const pointAt = (t: number) => {
    const theta = rad(90 + side * (LAUREL_START + t * LAUREL_SWEEP));
    return { x: 50 + LAUREL_R * Math.cos(theta), y: 50 + LAUREL_R * Math.sin(theta), theta };
  };

  for (let i = 0; i < LAUREL_LEAVES; i++) {
    const t = (i + 0.5) / LAUREL_LEAVES;
    const p = pointAt(t);
    // Tangent (direction of growth), then splay the leaf off it.
    const tangent = (Math.atan2(side * Math.cos(p.theta), -side * Math.sin(p.theta)) * 180) / Math.PI;
    const splay = i % 2 === 0 ? LEAF_SPLAY : -LEAF_SPLAY;
    const dir = tangent + splay * side;
    const scale = 1 - t * 0.35;
    const cx = p.x + Math.cos(rad(dir)) * 3.2 * scale;
    const cy = p.y + Math.sin(rad(dir)) * 3.2 * scale;
    leaves.push(
      <ellipse key={i} cx={cx} cy={cy} rx={1.9 * scale} ry={4.4 * scale} fill={color} transform={`rotate(${dir - 90} ${cx} ${cy})`} />,
    );
  }

  const a = pointAt(0);
  const b = pointAt(1);
  const stem = `M${a.x.toFixed(2)} ${a.y.toFixed(2)}A${LAUREL_R} ${LAUREL_R} 0 0 ${side > 0 ? 1 : 0} ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
  return (
    <g key={side}>
      <path d={stem} fill="none" stroke={color} stroke-width="1.1" stroke-linecap="round" />
      {leaves}
    </g>
  );
}

function bezelTicks(): JSX.Element[] {
  const ticks: JSX.Element[] = [];
  for (let i = 0; i < MEDAL_TICKS; i++) {
    const a = (i / MEDAL_TICKS) * Math.PI * 2;
    const r0 = 44.2;
    const r1 = i % 4 === 0 ? 48.6 : 47.2;
    ticks.push(
      <line key={i} x1={50 + Math.cos(a) * r0} y1={50 + Math.sin(a) * r0} x2={50 + Math.cos(a) * r1} y2={50 + Math.sin(a) * r1} />,
    );
  }
  return ticks;
}

/** Ink that reads on the disc: the secondary colour unless it is too close in lightness. */
function inkOn(colors: { primary: string; secondary: string }): string {
  const lumGap = Math.abs(luminance(colors.primary) - luminance(colors.secondary));
  if (lumGap > 0.18) {
    return colors.secondary;
  }
  return luminance(colors.primary) > 0.55 ? '#1b1510' : '#f6efdc';
}

export function Medallion({ colors, mark, size, relation, laurel, flag, glyph, badge, title }: MedallionProps) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const ring = relation ? RELATION_COLOR[relation] : null;
  const discR = ring ? 36.5 : 40;
  const ink = inkOn(colors);
  const light = luminance(colors.primary) > LIGHT_DISC;

  // Leaders get gold lettering (dark bronze on light discs, engraved rather than embossed);
  // states print their ISO3 in the disc's ink.
  let fill = ink;
  if (laurel) {
    fill = light ? `url(#k${id})` : `url(#t${id})`;
  }
  const laurelInk = light ? '#3b2a10' : '#e6c77a';
  const relief = light && laurel ? -1 : 1;
  const fontSize = MARK_FONT[Math.min(mark.length, MARK_FONT.length - 1)];
  const fit = mark.length >= 3 ? { textLength: discR * MARK_FIT, lengthAdjust: 'spacingAndGlyphs' } : {};
  const markStyle = { font: `700 ${fontSize}px Cinzel, Georgia, serif` };
  const markText = (dy: number, paint: string, opacity: number) => (
    <text x="50" y={50 + dy} dy="0.36em" text-anchor="middle" fill={paint} opacity={opacity} style={markStyle} {...fit}>
      {mark}
    </text>
  );

  return (
    <span class="wc-emblem" style={{ width: `${size}px`, height: `${size}px` }} title={title}>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <defs>
          <radialGradient id={`d${id}`} cx="38%" cy="30%" r="80%">
            <stop offset="0" stop-color={shade(colors.primary, 0.22)} />
            <stop offset="0.55" stop-color={colors.primary} />
            <stop offset="1" stop-color={shade(colors.primary, -0.55)} />
          </radialGradient>
          <linearGradient id={`g${id}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#fbe7b0" />
            <stop offset="0.35" stop-color="#c9a45c" />
            <stop offset="0.65" stop-color="#7d5d28" />
            <stop offset="1" stop-color="#e6c77a" />
          </linearGradient>
          <linearGradient id={`t${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0.1" stop-color="#fff3cc" />
            <stop offset="0.5" stop-color="#e6c77a" />
            <stop offset="0.9" stop-color="#9c7a3c" />
          </linearGradient>
          <linearGradient id={`k${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0.1" stop-color="#6b4a18" />
            <stop offset="0.9" stop-color="#2a1c08" />
          </linearGradient>
          <clipPath id={`c${id}`}>
            <circle cx="50" cy="50" r={discR} />
          </clipPath>
          <filter id={`b${id}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" />
          </filter>
          <filter id={`n${id}`} x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.16 0" />
          </filter>
        </defs>
        <circle cx="50" cy="52" r="49" fill="rgba(0,0,0,.45)" />
        <circle cx="50" cy="50" r="46.5" fill="#15110b" stroke={`url(#g${id})`} stroke-width="5" />
        <g stroke="#3b2a10" stroke-width="0.9" opacity="0.7">{bezelTicks()}</g>
        {ring ? <circle cx="50" cy="50" r="40.5" fill="none" stroke={ring} stroke-width="5" /> : null}
        {ring ? <circle cx="50" cy="50" r="42.8" fill="none" stroke="rgba(0,0,0,.45)" stroke-width="0.8" /> : null}

        <g clip-path={`url(#c${id})`}>
          <circle cx="50" cy="50" r={discR} fill={`url(#d${id})`} />
          {flag ? <image href={flag} x={50 - discR} y={50 - discR} width={discR * 2} height={discR * 2} preserveAspectRatio="xMidYMid slice" /> : null}
          <rect x="0" y="0" width="100" height="100" filter={`url(#n${id})`} opacity="0.5" />
          <circle cx="50" cy="52.5" r={discR + 2} fill="none" stroke="#000" stroke-opacity="0.55" stroke-width="7" filter={`url(#b${id})`} />
        </g>
        {flag || laurel ? null : <circle cx="50" cy="50" r={discR - 3.4} fill="none" stroke={colors.secondary} stroke-opacity="0.45" stroke-width="1.1" />}
        {laurel ? <g opacity="0.85">{laurelBranch(1, laurelInk)}{laurelBranch(-1, laurelInk)}</g> : null}

        {glyph && !flag ? (
          <g style={{ color: ink }} transform={`translate(${50 - GLYPH_SIZE / 2} ${50 - GLYPH_SIZE / 2})`}>
            <Icon name={glyph} size={GLYPH_SIZE} />
          </g>
        ) : null}
        {flag || glyph ? null : (
          <g>
            {markText(EMBOSS_DY * relief, '#000', 0.4)}
            {markText(-EMBOSS_DY * relief, '#fff', relief > 0 ? 0.22 : 0.45)}
            {markText(0, fill, 1)}
          </g>
        )}
        <circle cx="50" cy="50" r={discR} fill="none" stroke="rgba(0,0,0,.55)" stroke-width="1" />
      </svg>
      {badge && relation ? (
        <span class="wc-medal-badge" style={{ background: RELATION_COLOR[relation] }}>
          <Icon name={badge} size={Math.max(10, Math.round(size * 0.24))} />
        </span>
      ) : null}
    </span>
  );
}

// ─── Floating tooltip ────────────────────────────────────────────────────────────────────

// Each useTip caller gets an id; only the caller that opened the tip may close it.
let tipOwner = 0;
let activeTip = 0;

/** Hover/focus handlers that show `render()` in the tooltip layer next to the element. */
export function useTip(render: () => ComponentChildren, side: TipState['side'] = 'below') {
  const me = useRef(0);
  if (me.current === 0) {
    tipOwner += 1;
    me.current = tipOwner;
  }

  const show = (e: { currentTarget: EventTarget | null }) => {
    const el = e.currentTarget as HTMLElement | null;
    if (!el) {
      return;
    }
    const r = el.getBoundingClientRect();
    activeTip = me.current;
    tip.value = { rect: { left: r.left, top: r.top, right: r.right, bottom: r.bottom }, content: render(), side };
  };
  const hide = () => {
    if (activeTip === me.current) {
      tip.value = null;
    }
  };

  // A hovered element that unmounts must not leave its tip behind.
  useEffect(() => hide, []);

  return { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide };
}

const TIP_GAP = 8;
const TIP_MARGIN = 8;

/** Renders the single tooltip; mounted once at the HUD root. */
export function TipLayer() {
  const ref = useRef<HTMLDivElement>(null);
  const state = tip.value;

  useLayoutEffect(() => {
    const el = ref.current;
    const root = el?.offsetParent as HTMLElement | null;
    if (!el || !root || !state) {
      return;
    }
    // Rects are in viewport pixels; the HUD may be zoomed (Hud uiScale), so convert to its units.
    const rootRect = root.getBoundingClientRect();
    const scale = root.offsetWidth > 0 ? rootRect.width / root.offsetWidth : 1;
    const box = { width: rootRect.width / scale, height: rootRect.height / scale };
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const a = {
      left: (state.rect.left - rootRect.left) / scale,
      right: (state.rect.right - rootRect.left) / scale,
      top: (state.rect.top - rootRect.top) / scale,
      bottom: (state.rect.bottom - rootRect.top) / scale,
    };
    const cx = (a.left + a.right) / 2;
    const cy = (a.top + a.bottom) / 2;

    let x = cx - w / 2;
    let y = a.bottom + TIP_GAP;
    if (state.side === 'above') {
      y = a.top - h - TIP_GAP;
    } else if (state.side === 'left') {
      x = a.left - w - TIP_GAP;
      y = cy - h / 2;
    } else if (state.side === 'right') {
      x = a.right + TIP_GAP;
      y = cy - h / 2;
    }

    // Flip vertically when out of room, then clamp inside the HUD.
    if (state.side === 'below' && y + h > box.height - TIP_MARGIN) {
      y = a.top - h - TIP_GAP;
    }
    if (state.side === 'above' && y < TIP_MARGIN) {
      y = a.bottom + TIP_GAP;
    }
    x = Math.min(Math.max(TIP_MARGIN, x), box.width - w - TIP_MARGIN);
    y = Math.min(Math.max(TIP_MARGIN, y), box.height - h - TIP_MARGIN);
    el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
    el.style.visibility = 'visible';
  }, [state]);

  if (!state) {
    return null;
  }
  return (
    <div ref={ref} class="wc-tipbox" style={{ left: 0, top: 0, visibility: 'hidden' }}>
      <Corners />
      {state.content}
    </div>
  );
}

// ─── Sources ─────────────────────────────────────────────────────────────────────────────

const TIP_SOURCES_MAX = 4;

/** Opens the Sources drawer on these sources. */
export function openSources(ids: readonly SourceId[]): void {
  sourceFocus.value = [...ids];
  tip.value = null;
}

interface SourceChipProps {
  ids: readonly SourceId[];
  /** Data year shown on the chip (greyed when stale). */
  year?: number;
  /** Date the year is judged against; defaults to the app date. */
  at?: DateStr;
  /** Shown after the year, e.g. 'est.' for a modelled value. */
  tag?: string;
  /** 'mini' = book icon only, the year appears when stale (dense rows like the top bar). */
  variant?: 'full' | 'mini';
}

/** Book chip: hover lists publishers, click opens the Sources drawer on these sources. */
export function SourceChip({ ids, year, at, tag, variant = 'full' }: SourceChipProps) {
  const { world, state } = useServices();
  const key = ids.join('|');

  useEffect(() => registerSources(ids), [key]);

  const handlers = useTip(() => <SourceTipBody ids={ids} />, 'below');

  if (ids.length === 0) {
    return (
      <span class="wc-chip wc-chip--empty" title="No source: value not shown">
        <Icon name="source" size={11} />∅
      </span>
    );
  }

  const stale = year !== undefined && isStale(year, at ?? state.date.value);
  let text = chipText(year, ids.length, tag);
  if (variant === 'mini' && !stale) {
    text = '';
  }
  const first = world.source(ids[0]);
  return (
    <button
      type="button"
      class={['wc-chip', stale ? 'wc-stale' : '', text ? '' : 'wc-chip--icon'].join(' ')}
      aria-label={`Sources: ${first?.publisher ?? ids[0]}${ids.length > 1 ? ` and ${ids.length - 1} more` : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        openSources(ids);
      }}
      {...handlers}
    >
      <Icon name="source" size={11} />
      {text}
    </button>
  );
}

function SourceTipBody({ ids }: { ids: readonly SourceId[] }) {
  const { world } = useServices();
  const shown = ids.slice(0, TIP_SOURCES_MAX);
  return (
    <>
      <div class="wc-tip-title">Sources</div>
      {shown.map((id) => {
        const s = world.source(id);
        return (
          <div class="wc-tip-src" key={id}>
            <b>{s?.publisher ?? 'Unresolved id'}</b>
            <span>{s?.title ?? id}</span>
          </div>
        );
      })}
      {ids.length > shown.length ? <div class="wc-year">+{ids.length - shown.length} more</div> : null}
      <div class="wc-tip-hint">Click to open in Sources</div>
    </>
  );
}

// ─── Claim labels (truth rule 6) ─────────────────────────────────────────────────────────

export type ClaimKind = 'data' | 'official' | 'assessment' | 'unverified';

export const CLAIM_TEXT: Record<ClaimKind, string> = {
  data: 'Data',
  official: 'Official claim',
  assessment: 'Independent assessment',
  unverified: 'Unverified',
};

/** `text` replaces the class name when the label also says where the claim comes from. */
export function ClaimLabel({ kind, text }: { kind: ClaimKind; text?: string }) {
  return <span class={`wc-claim wc-claim--${kind}`}>{text ?? CLAIM_TEXT[kind]}</span>;
}

/** Wraps a value; greys it and appends its year when older than 3 years at `at`. */
export function Aged({ year, at, children }: { year: number; at: DateStr; children: ComponentChildren }) {
  if (!isStale(year, at)) {
    return <>{children}</>;
  }
  return (
    <span class="wc-stale" title={`Latest data is from ${year}`}>
      {children} <span class="wc-year">({year})</span>
    </span>
  );
}

// ─── Relation tag ────────────────────────────────────────────────────────────────────────

const RELATION_ICON: Record<Relation, IconName> = {
  self: 'star',
  war: 'war',
  denounced: 'denounced',
  neutral: 'neutral',
  friend: 'friend',
  ally: 'ally',
};

export function relationIcon(r: Relation): IconName {
  return RELATION_ICON[r];
}

export function RelationTag({ relation }: { relation: Relation }) {
  return (
    <span class="wc-rel" style={{ color: RELATION_COLOR[relation], borderColor: RELATION_COLOR[relation] }}>
      <Icon name={RELATION_ICON[relation]} size={14} />
      {RELATION_LABEL[relation]}
    </span>
  );
}

// ─── Full-screen shell ───────────────────────────────────────────────────────────────────

interface ScreenProps {
  title: string;
  subtitle?: ComponentChildren;
  icon: IconName;
  onClose: () => void;
  class?: string;
  children: ComponentChildren;
}

export function Screen({ title, subtitle, icon, onClose, class: cls, children }: ScreenProps) {
  return (
    <div class="wc-screen-wrap">
      <div class="wc-backdrop" onClick={onClose} />
      <Frame large="large" class={`wc-screen ${cls ?? ''}`}>
        <header class="wc-screen-head">
          <span class="wc-screen-emblem">
            <Icon name={icon} size={26} />
          </span>
          <div class="wc-screen-titles">
            <h1 class="wc-title wc-screen-title">{title}</h1>
            {subtitle ? <div class="wc-screen-sub">{subtitle}</div> : null}
          </div>
        </header>
        <button type="button" class="wc-ibtn wc-close" aria-label="Close" onClick={onClose}>
          <Icon name="close" size={16} />
        </button>
        <div class="wc-screen-body">{children}</div>
      </Frame>
    </div>
  );
}

/** '28 Sep 2026' with a calendar glyph, for dated claims. */
export function AsOf({ date }: { date: DateStr }) {
  return (
    <span class="wc-asof">
      <Icon name="calendar" size={12} />
      as of {fmtDate(date)}
    </span>
  );
}
